import React from 'react';

/**
 * VehicleCardSkeleton — Pixel-accurate placeholder matching the real VehicleCard layout
 * Eliminates CLS by reserving exact space before vehicles load
 */
export function VehicleCardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden" aria-hidden="true">
      {/* Image placeholder — 4:3 aspect ratio matches .vehicle-card-img-wrap */}
      <div
        className="relative w-full bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] animate-pulse"
        style={{ aspectRatio: '4/3' }}
      />

      {/* Content */}
      <div className="p-5">
        {/* Title */}
        <div className="h-7 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse mb-1" />

        {/* Trim/Stock placeholder */}
        <div className="mb-3">
          <div className="h-4 w-32 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
        </div>

        {/* Price */}
        <div className="mb-3">
          <div className="h-8 w-36 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse mb-1.5" />
          <div className="h-5 w-48 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
        </div>

        {/* Key Stats — 2x2 grid */}
        <div className="grid grid-cols-2 gap-2 mb-4 pb-4 border-b border-[#E8E2D6]">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-5 h-5 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
              <div className="flex-1">
                <div className="h-3 w-12 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse mb-1" />
                <div className="h-4 w-20 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-6 w-20 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded-full animate-pulse"
            />
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mt-4">
          <div className="flex-1 h-10 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded-lg animate-pulse" />
          <div className="w-10 h-10 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded-lg animate-pulse" />
        </div>
      </div>
    </div>
  );
}

/**
 * FeaturedVehiclesSkeleton — Section-level skeleton matching FeaturedVehicles layout
 * Includes header + 6-card grid with identical spacing
 */
export function FeaturedVehiclesSkeleton() {
  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      aria-busy="true"
      aria-label="Loading vehicles"
    >
      {/* Visually-hidden loading announcement for screen readers */}
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        Loading vehicles, please wait…
      </p>

      {/* Header skeleton */}
      <div className="text-center mb-8" aria-hidden="true">
        <div className="h-10 w-80 mx-auto bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse mb-2" />
        <div className="h-4 w-96 mx-auto bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
      </div>

      {/* Grid — matches grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" aria-hidden="true">
        {[...Array(6)].map((_, i) => (
          <VehicleCardSkeleton key={i} />
        ))}
      </div>

      {/* Tagline strip skeleton */}
      <div className="flex items-start gap-3 bg-[#F7F5F0] border border-[#E8E2D6] rounded-lg px-5 py-4 mb-8" aria-hidden="true">
        <div className="w-5 h-5 bg-gradient-to-r from-[#E8E2D6] via-[#D8D0BC] to-[#E8E2D6] rounded-full animate-pulse shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-full bg-gradient-to-r from-[#E8E2D6] via-[#D8D0BC] to-[#E8E2D6] rounded animate-pulse" />
          <div className="h-3 w-5/6 bg-gradient-to-r from-[#E8E2D6] via-[#D8D0BC] to-[#E8E2D6] rounded animate-pulse" />
        </div>
      </div>

      {/* Browse By filter shortcuts skeleton */}
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-3 text-sm" aria-hidden="true">
        <div className="h-4 w-20 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
        {[...Array(6)].map((_, i) => (
          <>
            <div key={i} className="h-8 w-24 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded-full animate-pulse" />
            {i < 5 && <span className="text-[#D8D0BC]">·</span>}
          </>
        ))}
      </div>
    </div>
  );
}
